/**
 * Reusable User Table Component
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Lock as LockIcon } from '@mui/icons-material';
import { User } from '../types';

interface UserTableProps {
  users: User[];
  loading: boolean;
  onEditUser: (user: User) => void;
  onChangePassword: (user: User) => void;
  onDeleteUser: (user: User) => void;
}

const UserTable: React.FC<UserTableProps> = ({ users, loading, onEditUser, onChangePassword, onDeleteUser }) => {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'content_user':
        return 'info';
      case 'user':
        return 'warning';
      case 'tech_support':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No users found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Position</TableCell>
            <TableCell>Museums</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Joined</TableCell>
            <TableCell>Last Login</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(user => (
            <TableRow key={user.id}>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{`${user.first_name} ${user.last_name}`.trim() || '-'}</TableCell>
              <TableCell>{user.phone_number || '-'}</TableCell>
              <TableCell>{user.department || '-'}</TableCell>
              <TableCell>{user.position || '-'}</TableCell>
              <TableCell>{user.museum_names?.length ? user.museum_names.join(', ') : user.museum_name || '-'}</TableCell>
              <TableCell>
                <Chip label={user.role_display} color={getRoleColor(user.role) as any} size="small" />
              </TableCell>
              <TableCell>
                <Chip
                  label={user.is_active ? 'Active' : 'Inactive'}
                  color={user.is_active ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>{formatDate(user.date_joined)}</TableCell>
              <TableCell>{user.last_login ? formatDate(user.last_login) : 'Never'}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => onEditUser(user)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onChangePassword(user)}>
                  <LockIcon />
                </IconButton>
                {user.username !== 'admin' && (
                  <IconButton size="small" color="error" onClick={() => onDeleteUser(user)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserTable;
